import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { SERVER_URL, cn } from '../../util/EnvironmentConfig';
import { Stepper } from './components/Stepper';
import { Input } from './components/Input';
import { Label } from './components/Label';
import { Textarea } from './components/Textarea';
import {
  Check,
  ArrowRight,
  ArrowLeft,
  UploadCloud,
  Eye,
  EyeOff,
} from 'lucide-react';
import Button from '../../components/Button';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// Zod Validation Schemas
const MAX_IMAGE_SIZE = 16 * 1024 * 1024; // 16MB

const step1Schema = z
  .object({
    profileImage: z.any().optional(), // Base64 string from upload
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Valid email is required'),
    contact: z.string().min(1, 'Contact number is required'),
    password: z.string().min(8, 'Password must be at least 8 characters long'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

const step2Schema = z.object({
  logo: z
    .any()
    .refine((val) => val !== null && val !== '', 'Business logo is required'),
  businessName: z.string().min(1, 'Business name is required'),
  businessEmail: z.string().email('Valid business email is required'),
  businessContact: z.string().optional(),
  description: z.string().optional(),
  website: z
    .string()
    .url('Must be a valid URL (e.g., https://example.com)')
    .optional()
    .or(z.literal('')),
  parentCategory: z.string().min(1, 'Please select a category'),
  category: z.string().min(1, 'Please select a subcategory'),
});

function Signup({ registerBtn }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [categoryList, setCategoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const navigate = useNavigate();

  // Fetch categorized taxonomy
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${SERVER_URL}/context/get-all-category?limit=0`,
        );
        setCategoryList(response.data.data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Group categories by Vertical > CategoryName
  const groupedCategories = categoryList.reduce((acc, cat) => {
    const vertical = cat.vertical || 'Other';
    const parent = cat.categoryName || 'Uncategorized';

    if (!acc[vertical]) acc[vertical] = {};
    if (!acc[vertical][parent]) acc[vertical][parent] = [];

    acc[vertical][parent].push(cat);
    return acc;
  }, {});

  // Form Hooks
  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    formState: { errors: errorsStep1 },
    watch: watchStep1,
    setValue: setValueStep1,
  } = useForm({
    resolver: zodResolver(step1Schema),
    mode: 'onChange',
    defaultValues: {
      profileImage: '',
      firstName: '',
      lastName: '',
      email: '',
      contact: '',
      password: '',
      confirmPassword: '',
    },
  });

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    formState: { errors: errorsStep2 },
    watch: watchStep2,
    setValue: setValueStep2,
  } = useForm({
    resolver: zodResolver(step2Schema),
    mode: 'onChange',
    defaultValues: {
      logo: '',
      businessName: '',
      businessEmail: '',
      businessContact: '',
      description: '',
      website: '',
      parentCategory: '',
      category: '',
    },
  });

  const onNextStep = () => {
    // Save Step 1 data to state or context if needed, but react-hook-form holds it
    setCurrentStep(2);
  };

  const onFinalSubmit = async (dataStep2) => {
    setIsLoading(true);
    try {
      const dataStep1 = watchStep1();

      const submitData = {
        // Step 1
        firstName: dataStep1.firstName,
        lastName: dataStep1.lastName,
        email: dataStep1.email,
        contact: dataStep1.contact,
        password: dataStep1.password,
        profileImage: dataStep1.profileImage,

        // Step 2
        name: dataStep2.businessName,
        businessEmail: dataStep2.businessEmail,
        businessContact: dataStep2.businessContact,
        description: dataStep2.description,
        website: dataStep2.website,
        logo: dataStep2.logo,

        // We ensure category is mapped safely
        category: [dataStep2.category],
      };

      await axios.post(
        `${SERVER_URL}/business/businessRegistration`,
        submitData,
      );
      toast.success('Thank you for signing up with KnowQuest!');
      navigate('/businesslogin');
    } catch (error) {
      if (error.response && error.response.data.websiteExists) {
        toast.error(
          'This website is already registered. Please use a different website.',
        );
      } else {
        toast.error('There was an error signing up! Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e, setFormValue, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 16MB');
      return;
    }

    if (
      !file.type.startsWith('image/png') &&
      !file.type.startsWith('image/jpeg')
    ) {
      toast.error('Only PNG and JPEG images are allowed');
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setFormValue(fieldName, reader.result, { shouldValidate: true });
    };
  };

  const currentProfileImage = watchStep1('profileImage');
  const currentLogo = watchStep2('logo');
  const selectedParentCategory = watchStep2('parentCategory');

  // Filter subCategories based on selected parent (categoryName)
  const subCategoriesForSelectedParent = categoryList.filter(
    (cat) => (cat.categoryName || 'Uncategorized') === selectedParentCategory,
  );

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary">
        <div className="w-full max-w-4xl bg-[#111827] shadow-2xl rounded-3xl overflow-hidden border border-slate-800">
          <div className="bg-gradient-to-br from-[#081325] to-[#112140] p-8 md:p-12 text-white relative flex flex-col items-center">
            <button
              type="button"
              onClick={registerBtn}
              className="absolute left-8 top-8 flex items-center text-sm gap-1 text-white/70 hover:text-white transition-colors"
            >
              <ArrowBackIosNewIcon fontSize="small" />
              Back
            </button>

            <h1 className="text-3xl md:text-5xl font-bold text-center mt-6">
              Create Business Account
            </h1>
            <p className="mt-4 text-blue-200 text-center max-w-lg">
              Join KnowQuest to get insights on your business. Set up your
              profile in two simple steps.
            </p>
          </div>

          <div className="p-8 md:p-12">
            <Stepper
              steps={['Personal Info', 'Business Info']}
              currentStep={currentStep}
            />

            <div className="mt-10">
              {currentStep === 1 && (
                <form
                  onSubmit={handleSubmitStep1(onNextStep)}
                  className="space-y-6 animate-in slide-in-from-right-4 duration-500"
                >
                  {/* Profile Image Section */}
                  <div className="flex flex-col items-center mb-8">
                    <div className="relative group cursor-pointer">
                      <div className="w-28 h-28 rounded-full border-4 border-dashed border-slate-700 overflow-hidden bg-slate-800/50 flex flex-col items-center justify-center group-hover:border-blue-500 transition-colors">
                        {currentProfileImage ? (
                          <img
                            src={currentProfileImage}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex flex-col items-center text-slate-400">
                            <UploadCloud className="w-8 h-8 mb-1 text-slate-400 group-hover:text-blue-500" />
                            <span className="text-[10px] uppercase font-bold text-center">
                              Add Photo
                            </span>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={(e) =>
                          handleImageUpload(e, setValueStep1, 'profileImage')
                        }
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-3 font-medium">
                      (Optional) Personal Profile Picture
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">
                        First Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="firstName"
                        placeholder="John"
                        {...registerStep1('firstName')}
                      />
                      {errorsStep1.firstName && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep1.firstName.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">
                        Last Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        {...registerStep1('lastName')}
                      />
                      {errorsStep1.lastName && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep1.lastName.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        {...registerStep1('email')}
                      />
                      {errorsStep1.email && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep1.email.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact">
                        Contact Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contact"
                        placeholder="+1 (555) 000-0000"
                        {...registerStep1('contact')}
                      />
                      {errorsStep1.contact && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep1.contact.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 relative">
                      <Label htmlFor="password">
                        Password <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...registerStep1('password')}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-300"
                        >
                          {showPassword ? (
                            <Eye className="w-5 h-5" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {errorsStep1.password && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep1.password.message}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 relative">
                      <Label htmlFor="confirmPassword">
                        Confirm Password <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...registerStep1('confirmPassword')}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfPassword(!showConfPassword)}
                          className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-300"
                        >
                          {showConfPassword ? (
                            <Eye className="w-5 h-5" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {errorsStep1.confirmPassword && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep1.confirmPassword.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-6 flex justify-end">
                    <Button
                      type="submit"
                      className="w-full md:w-auto px-8 py-3 flex items-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all font-semibold"
                    >
                      Next Step <ArrowRight className="w-5 h-5" />
                    </Button>
                  </div>
                </form>
              )}

              {currentStep === 2 && (
                <form
                  onSubmit={handleSubmitStep2(onFinalSubmit)}
                  className="space-y-6 animate-in slide-in-from-right-4 duration-500"
                >
                  {/* Business Logo Section */}
                  <div className="flex flex-col items-center mb-8">
                    <div className="relative group cursor-pointer">
                      <div
                        className={cn(
                          'w-32 h-32 rounded-2xl border-4 border-dashed overflow-hidden bg-slate-800/50 flex flex-col items-center justify-center transition-colors',
                          errorsStep2.logo
                            ? 'border-red-400'
                            : 'border-slate-700 group-hover:border-blue-500',
                        )}
                      >
                        {currentLogo ? (
                          <img
                            src={currentLogo}
                            alt="Business Logo"
                            className="w-full h-full object-contain p-2"
                          />
                        ) : (
                          <div className="flex flex-col items-center text-slate-400 p-4 text-center">
                            <UploadCloud className="w-8 h-8 mb-2 text-slate-400 group-hover:text-blue-500" />
                            <span className="text-xs font-bold text-slate-300">
                              Upload Logo *
                            </span>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/png, image/jpeg"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={(e) =>
                          handleImageUpload(e, setValueStep2, 'logo')
                        }
                      />
                    </div>
                    {errorsStep2.logo && (
                      <span className="text-xs text-red-500 font-medium mt-2">
                        {errorsStep2.logo.message}
                      </span>
                    )}
                    <p className="text-xs text-slate-400 mt-2 font-medium">
                      PNG or JPEG, max 16MB. Transparent background preferred.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="businessName">
                        Business Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="businessName"
                        placeholder="Acme Corp"
                        {...registerStep2('businessName')}
                      />
                      {errorsStep2.businessName && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep2.businessName.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessEmail">
                        Business Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="businessEmail"
                        type="email"
                        placeholder="contact@acme.com"
                        {...registerStep2('businessEmail')}
                      />
                      {errorsStep2.businessEmail && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep2.businessEmail.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="parentCategory">
                        Business Category{' '}
                        <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id="parentCategory"
                        {...registerStep2('parentCategory', {
                          onChange: () => {
                            setValueStep2('category', '');
                          },
                        })}
                        className="flex h-12 w-full rounded-xl border border-slate-700 bg-slate-800 text-white px-4 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <option value="">Select a category...</option>
                        {Object.keys(groupedCategories).map((vertical) => (
                          <optgroup
                            key={vertical}
                            label={`--- ${vertical.toUpperCase()} ---`}
                          >
                            {Object.keys(groupedCategories[vertical]).map(
                              (parent) => (
                                <option key={parent} value={parent}>
                                  {parent}
                                </option>
                              ),
                            )}
                          </optgroup>
                        ))}
                      </select>
                      {errorsStep2.parentCategory && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep2.parentCategory.message}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">
                        Subcategory <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id="category"
                        {...registerStep2('category')}
                        className="flex h-12 w-full rounded-xl border border-slate-700 bg-slate-800 text-white px-4 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
                        disabled={!selectedParentCategory}
                      >
                        <option value="">Select a subcategory...</option>
                        {subCategoriesForSelectedParent.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.subCategory || cat.title}
                          </option>
                        ))}
                      </select>
                      {errorsStep2.category && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep2.category.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="businessContact">
                        Business Phone{' '}
                        <span className="text-slate-500 font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <Input
                        id="businessContact"
                        placeholder="Office Number"
                        {...registerStep2('businessContact')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">
                        Website URL{' '}
                        <span className="text-slate-500 font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <Input
                        id="website"
                        type="url"
                        placeholder="https://acme.com"
                        {...registerStep2('website')}
                      />
                      {errorsStep2.website && (
                        <span className="text-xs text-red-500 font-medium">
                          {errorsStep2.website.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Business Description{' '}
                      <span className="text-slate-500 font-normal">
                        (Optional)
                      </span>
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Tell us briefly about your business..."
                      {...registerStep2('description')}
                      className="h-24 resize-none"
                    />
                  </div>

                  <div className="pt-6 flex justify-between items-center border-t border-slate-800">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex items-center gap-2 text-slate-400 hover:text-white font-medium transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" /> Back
                    </button>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full md:w-auto px-8 py-3 flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all font-semibold disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30"
                    >
                      {isLoading ? (
                        'Creating Account...'
                      ) : (
                        <>
                          {' '}
                          Complete Registration <Check className="w-5 h-5" />{' '}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Signup;
